/**
 * "My people" as a tree: a supervisor's direct reports, where anyone who supervises
 * others is one row carrying their whole team's rollup, opened with "See team".
 *
 * Shared by My team's metrics and the Supervisors sidebar, which show the same team
 * detail (Shayna, 2026-09-25 review: "just mirror exactly my team's metrics").
 */
import type { Person } from '@/data/selectors'
import type { Team } from './teams'

/** One line of "My people": a person, or a sub-supervisor's whole team. */
export type PeopleRow =
  | { kind: 'person'; p: Person; indent: number }
  | {
      kind: 'team'
      t: Team
      indent: number
      open: boolean
      /** Shown for the person, not expandable: a direct report's team in the direct-reports list. */
      fixed?: boolean
    }

/**
 * Who sits directly under each supervisor, read off every person's reporting line —
 * not off the people rows alone, because with management excluded a sub-supervisor
 * has no row of their own but still has a team. A name with a team is a sub-team;
 * anybody else is a person.
 */
export function childrenBySupervisor(pool: Person[], hasTeam: (name: string) => boolean) {
  const subs = new Map<string, Set<string>>()
  const people = new Map<string, Person[]>()
  const addSub = (parent: string, child: string) => {
    let s = subs.get(parent)
    if (!s) subs.set(parent, (s = new Set()))
    s.add(child)
  }
  for (const p of pool) {
    const line = p.supervisors
    const first = line[0]
    if (first && first !== p.employee) {
      if (hasTeam(p.employee)) addSub(first, p.employee)
      else {
        const list = people.get(first)
        if (list) list.push(p)
        else people.set(first, [p])
      }
    }
    for (let i = 1; i < line.length; i++) {
      const child = line[i - 1]!
      const parent = line[i]!
      if (child !== parent && hasTeam(child)) addSub(parent, child)
    }
  }
  return { subs, people }
}

/**
 * "My people" in drill-down order under `supervisor`: sub-teams first, by name, each
 * followed by its own people when it is in `open`; then people, lowest score first.
 */
export function peopleRows(
  children: ReturnType<typeof childrenBySupervisor>,
  treeByName: ReadonlyMap<string, Team>,
  open: ReadonlySet<string>,
  supervisor: string,
): PeopleRow[] {
  const out: PeopleRow[] = []
  const byScore = (a: Person, b: Person) =>
    (a.score.scored ? a.score.total : 99) - (b.score.scored ? b.score.total : 99)
  const walk = (sup: string, indent: number, seen: Set<string>) => {
    if (seen.has(sup)) return // a reporting loop in the data must not hang the page
    seen.add(sup)
    for (const name of [...(children.subs.get(sup) ?? [])].sort()) {
      const t = treeByName.get(name)
      if (!t) continue
      const isOpen = open.has(name)
      out.push({ kind: 'team', t, indent, open: isOpen })
      if (isOpen) walk(name, indent + 1, seen)
    }
    for (const p of [...(children.people.get(sup) ?? [])].sort(byScore))
      out.push({ kind: 'person', p, indent })
  }
  if (supervisor) walk(supervisor, 0, new Set())
  return out
}

/** "See team" / "hide team" on one sub-team row: the opened list with that name flipped. */
export function toggleOpen(openList: readonly string[], name: string): string[] {
  const next = new Set(openList)
  if (next.has(name)) next.delete(name)
  else next.add(name)
  return [...next].sort()
}
